// NodeMissionsEmpty.jsx

import React, { useMemo } from 'react';

// CSS
import './NodeMissionsEmpty.css';

// Components
import Button from 'components/button/Button';

/**
 * NodeMissionsEmpty
 *
 * Renders the empty state for the node-native Missions surface.
 *
 * @param {boolean} canManageMissions - Whether the current user can create Mission templates.
 * @param {boolean} isEditorOpen - Whether the Mission template editor is already open.
 * @param {function} onCreateMission - Opens the Mission template editor.
 * @param {string} pluralTypeLabel - Plural label for the active node type.
 * @param {string} singularTypeLabel - Singular label for the active node type.
 * @param {'compact'|'expanded'} addon - Layout density for peek vs full node views.
 *
 * @returns {ReactNode} NodeMissionsEmpty.
 */
const NodeMissionsEmpty = React.memo(({
  canManageMissions = false,
  isEditorOpen = false,
  onCreateMission,
  pluralTypeLabel = 'items',
  singularTypeLabel = 'item',
  addon = 'expanded',
}) => {

  // ----- CONTEXT ----- //

  const isCompact = addon === 'compact';

  const lowercasePluralTypeLabel = pluralTypeLabel.toLowerCase();
  const lowercaseSingularTypeLabel = singularTypeLabel.toLowerCase();

  // ----- MEMOS ----- //

  const headerCopy = useMemo(() => {
    if (canManageMissions) {
      return isCompact
        ? `Define playbook steps once for all ${lowercasePluralTypeLabel}, then launch and run the playbook against any ${lowercaseSingularTypeLabel}, at any time.`
        : `Playbooks are reusable processes — onboarding, handoffs, reviews, or any multi-step process your team runs more than once. Define the steps once, then launch and run the playbook against any ${lowercaseSingularTypeLabel}, at any time.`;
    }

    return isCompact
      ? 'Playbooks help your team run repeatable steps with clear owners and progress.'
      : `Playbooks help your team run repeatable processes — like onboarding or implementation — with clear steps, owners, and progress. Ask a space admin to create one for ${lowercasePluralTypeLabel}.`;
  }, [canManageMissions, isCompact, lowercasePluralTypeLabel, lowercaseSingularTypeLabel]);

  const cardTitle = useMemo(() => {
    if (canManageMissions) {
      return isCompact
        ? 'Turn recurring work into a playbook'
        : `Run repeatable processes for every ${singularTypeLabel}`;
    }

    return 'No playbooks set up yet';
  }, [canManageMissions, isCompact, singularTypeLabel]);

  const cardCopy = useMemo(() => {
    if (canManageMissions) {
      return isCompact
        ? `Create a playbook for ${lowercasePluralTypeLabel}, then launch it on any ${lowercaseSingularTypeLabel} from this tab.`
        : `Create a playbook for ${lowercasePluralTypeLabel}. Once saved, you can launch it on any ${lowercaseSingularTypeLabel} and track progress here.`;
    }

    return isCompact
      ? 'Ask a space admin to set one up for this database.'
      : `Only space admins can configure playbooks. Ask one to set up a playbook for ${lowercasePluralTypeLabel}.`;
  }, [canManageMissions, isCompact, lowercasePluralTypeLabel, lowercaseSingularTypeLabel]);

  const benefitItems = useMemo(() => {
    if (!canManageMissions || isCompact) return [];

    return [
      'Track progress across people and automation',
      `Reuse the same steps for every new ${lowercaseSingularTypeLabel}`,
      'Launch from any record and manage from here',
    ];
  }, [canManageMissions, isCompact, lowercaseSingularTypeLabel]);

  // ----- RENDER ----- //

  return (
    <div className={`node-missions-empty-container${isCompact ? ' node-missions-empty-container-compact' : ''}`}>

      {/* HEADER */}
      <div className="node-missions-empty-header">

        {/* HEADING GROUP */}
        <div className="node-missions-empty-heading-group">

          {/* DESCRIPTION */}
          <div
            className="node-missions-empty-description"
          >
            {headerCopy}
          </div>
        </div>
      </div>

      {/* EMPTY STATE */}
      <div
        className="node-missions-empty-state"
      >

        {/* EMPTY TITLE */}
        <div
          className="node-missions-empty-title"
        >
          {cardTitle}
        </div>

        {/* EMPTY COPY */}
        <div className="node-missions-empty-copy">
          {cardCopy}
        </div>

        {/* BENEFITS */}
        {benefitItems.length > 0 ? (

          <ul className="node-missions-empty-benefits">
            {benefitItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ) : null}

        {/* ACTIONS */}
        {canManageMissions ? (

          <div className="node-missions-empty-actions">
            <Button
              enabled={!isEditorOpen}
              height={isCompact ? 36 : 40}
              iconKey="add"
              label="Create a playbook"
              onClick={onCreateMission}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
});

// Dev Tools
NodeMissionsEmpty.displayName = 'NodeMissionsEmpty';

export default NodeMissionsEmpty;
